'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * Copy text to the clipboard with a graceful fallback to the legacy
 * `execCommand('copy')` path (for non-secure contexts / older browsers).
 * Exposes a transient `copied` flag for button affordances. Consolidates the
 * four ad-hoc copy handlers that previously lived in the preview components.
 */
export function useCopyToClipboard(resetMs = 2000): { copy: (text: string) => Promise<void>; copied: boolean } {
    const [copied, setCopied] = useState(false);

    const copy = useCallback(async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
            } finally {
                textarea.remove();
            }
        }
        setCopied(true);
    }, []);

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(setCopied, resetMs, false);
        return () => clearTimeout(timer);
    }, [copied, resetMs]);

    return { copy, copied };
}
